import { apiClient, API_CONFIG } from '../config/api'

// simService — thin REST client over the Java BM backend's
// SimCardResource. Powers the new SimAssignModal that replaces the
// legacy Ionic "coming soon" toast. All paths route through fleet-api's
// /api/bm proxy so service-to-service auth is rewritten upstream.

const BM = API_CONFIG.BM_BASE_URL

export interface SimCard {
  id?: number
  iccid: string
  phoneNumber?: string
  msisdn?: string
  operator?: string
  carrier?: string
  technology?: string
  country?: string
  status?: string
  plan?: string
  dataLimit?: number
  dataUsed?: number
  dataRemaining?: number
  expiresAt?: string
  // Pass-through for any extra backend fields we don't model yet.
  [k: string]: unknown
}

export interface SimListFilters {
  technology?: string
  operator?: string
  limit?: number
}

function normalizeSim(raw: any): SimCard {
  return {
    id: raw?.id ?? raw?.simId,
    iccid: raw?.iccid ?? raw?.simIccid ?? raw?.sim_iccid ?? '',
    phoneNumber:
      raw?.phoneNumber ?? raw?.phone_number ?? raw?.simPhoneNumber ?? raw?.sim_phone_number ?? raw?.msisdn,
    msisdn: raw?.msisdn,
    operator: raw?.operator ?? raw?.carrier,
    carrier: raw?.carrier,
    technology: raw?.technology ?? raw?.tech,
    country: raw?.country ?? raw?.countryCode ?? raw?.country_code,
    status: raw?.status,
    plan: raw?.plan ?? raw?.planName ?? raw?.plan_name,
    dataLimit: raw?.dataLimit ?? raw?.data_limit ?? raw?.dataLimitMb,
    dataUsed: raw?.dataUsed ?? raw?.data_used ?? raw?.dataUsedMb,
    dataRemaining: raw?.dataRemaining ?? raw?.data_remaining ?? raw?.dataRemainingMb,
    expiresAt: raw?.expiresAt ?? raw?.expires_at,
    ...raw,
  }
}

export const simService = {
  async listAvailable(filters: SimListFilters = {}): Promise<SimCard[]> {
    const params: Record<string, string | number> = {}
    if (filters.technology) params.technology = filters.technology
    if (filters.operator)   params.operator   = filters.operator
    params.limit = filters.limit ?? 200
    const { data } = await apiClient.get(`${BM}/simcards/available`, params)
    if (Array.isArray(data)) return data.map(normalizeSim)
    if (Array.isArray(data?.data)) return data.data.map(normalizeSim)
    return []
  },

  // assignSimToBalise expects ?simIccid=&simPhoneNumber=. We pass them as
  // query string because the Java endpoint reads @QueryParam.
  async assignToBalise(universalId: string, sim: { iccid: string; phoneNumber?: string }) {
    const qs = new URLSearchParams({ simIccid: sim.iccid })
    if (sim.phoneNumber) qs.set('simPhoneNumber', sim.phoneNumber)
    const { data } = await apiClient.post(
      `${BM}/balises/${encodeURIComponent(universalId)}/sim?${qs.toString()}`,
    )
    return data
  },

  async unassignFromBalise(universalId: string) {
    const { data } = await apiClient.delete(`${BM}/balises/${encodeURIComponent(universalId)}/sim`)
    return data
  },
}

export default simService
